import { useRFQQuoteSubmission } from "./hooks/useRFQQuoteSubmission";
import { rfqData } from "./config/rfqQuoteSubmission.config";
import { QuoteHeader, QuoteDetailsForm, QuoteSidebar } from "./components";
import { useEffect, useState } from "react";
import { rfqService, adminService } from "@b2b/services";
import { Rfq } from "@b2b/services/api/modules/rfq/rfq.types";
import { XCircle, X } from "lucide-react";

interface RFQ_QuoteSubmissionPageProps {
  id?: string;
  navigate: (path: string) => void;
}

interface ToastState {
  type: "success" | "error";
  message: string;
}

export default function RFQ_QuoteSubmissionPage({
  id,
  navigate,
}: RFQ_QuoteSubmissionPageProps) {
  const { formData, filesMap, setFilesMap, handleChange } =
    useRFQQuoteSubmission();
  const [rfq, setRfq] = useState<Rfq | null>(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [toast, setToast] = useState<ToastState | null>(null);

  const showToast = (type: "success" | "error", message: string) => {
    setToast({ type, message });
    setTimeout(() => setToast(null), 5000);
  };

  useEffect(() => {
    if (!id) return;

    const fetchRfq = async () => {
      try {
        setLoading(true);
        const response = await rfqService.getRfqDetail(id);
        if (response.data) {
          setRfq(response.data);
        }
      } catch (error: any) {
        console.error("Error fetching RFQ:", error);
        showToast(
          "error",
          error?.response?.data?.message || "Failed to load RFQ"
        );
      } finally {
        setLoading(false);
      }
    };

    fetchRfq();
  }, [id]);

  const handleSubmit = async () => {
    if (!id) return;

    try {
      setSubmitting(true);

      // Upload files first if any (using media service)
      const attachments: string[] = [];
      const filesToUpload: File[] = [];

      // Collect all files from filesMap
      Object.values(filesMap).forEach((fileArray) => {
        filesToUpload.push(...fileArray);
      });

      // Upload files and get media IDs
      if (filesToUpload.length > 0) {
        for (const file of filesToUpload) {
          try {
            // Validate file size (10MB limit)
            const maxSize = 10 * 1024 * 1024; // 10MB
            if (file.size > maxSize) {
              showToast("error", `File ${file.name} exceeds 10MB size limit`);
              continue;
            }

            // Validate file type
            const allowedTypes = [
              "application/pdf",
              "image/png",
              "image/jpeg",
              "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
            ];
            if (!allowedTypes.includes(file.type)) {
              showToast(
                "error",
                `File ${file.name} has invalid type. Allowed: PDF, PNG, JPG, XLSX`
              );
              continue;
            }

            const result = await adminService.uploadMedia(file);
            if (result?.id) {
              attachments.push(result.id);
            }
          } catch (error: any) {
            console.error(`Error uploading file ${file.name}:`, error);
            showToast(
              "error",
              `Failed to upload ${file.name}: ${error?.response?.data?.message || error.message}`
            );
          }
        }
      }

      const payload = {
        price: parseFloat(formData.price as string) || 0,
        currency: (formData.currency as string) || "USD",
        leadTimeDays: parseInt(formData.leadTimeDays as string) || 0,
        message: formData.message as string,
        attachments,
      };

      await rfqService.postQuote(id, payload);
      showToast("success", "Quote submitted successfully!");
      navigate(`/rfq/${id}`);
    } catch (error: any) {
      console.error("Error submitting quote:", error);
      showToast(
        "error",
        error?.response?.data?.message || "Failed to submit quote"
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Toast Notification */}
      {toast && (
        <div
          className={`fixed top-4 right-4 z-50 flex items-center gap-2 p-4 rounded-lg shadow-lg max-w-[400px] ${
            toast.type === "success"
              ? "bg-green-500 text-white"
              : "bg-red-500 text-white"
          }`}
        >
          <XCircle className="w-5 h-5 flex-shrink-0" />
          <p className="flex-1 font-medium text-sm">{toast.message}</p>
          <button
            onClick={() => setToast(null)}
            className="ml-2 hover:opacity-80 transition-opacity flex-shrink-0"
            aria-label="Close notification"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      <main className="container mx-auto px-4 py-6">
        {loading ? (
          <div className="text-center py-16">
            <p className="text-gray-500 text-lg">Loading RFQ...</p>
          </div>
        ) : rfq ? (
          <>
            <QuoteHeader
              id={id}
              title={rfq.title}
              company={rfq.buyer?.company?.name || "N/A"}
              quantity={rfq.quantity.toString()}
              location={rfq.buyer?.company?.name || "N/A"}
            />

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <QuoteDetailsForm
                formData={formData}
                filesMap={filesMap}
                setFilesMap={setFilesMap}
                handleChange={handleChange}
                onSubmit={handleSubmit}
                submitting={submitting}
              />
              <QuoteSidebar
                formData={formData}
                navigate={navigate}
                onSubmit={handleSubmit}
                submitting={submitting}
              />
            </div>
          </>
        ) : (
          <div className="text-center py-16">
            <p className="text-gray-500 text-lg">RFQ not found</p>
          </div>
        )}
      </main>
    </div>
  );
}
